<script setup lang="ts">
import { useI18n } from '@/shared/i18n'
import { Button, Icon } from '@/shared/ui'

defineOptions({ name: 'GridBlockActions' })

/**
 * The edit overlay over a nested block — v3's hover sheet (`CustomDashboardSection.vue:166-178`)
 * with Move / Duplicate / Delete / Edit. Over a block with no widget yet it stays visible, since
 * there is nothing underneath to show; over a rendered widget it appears on hover or focus.
 */
defineProps<{
  rowId: string
  /** The cell has no widget: the overlay is the block's whole content. */
  empty: boolean
  /** The widget kind can be reopened in the builder (an empty block always can). */
  editable: boolean
}>()
const emit = defineEmits<{
  'move-start': [event: DragEvent]
  'move-end': []
  duplicate: []
  remove: []
  edit: []
}>()
const { t } = useI18n()
</script>

<template>
  <div
    class="grid-block-actions absolute inset-0 z-raised flex items-start justify-center rounded-md px-xs pt-xl transition-opacity"
    :class="
      empty
        ? 'border border-dashed border-border-strong bg-surface-sunken'
        : 'bg-surface/80 opacity-0 backdrop-blur-xs group-hover/cell:opacity-100 focus-within:opacity-100'
    "
  >
    <div
      class="grid-block-actions__bar flex flex-wrap items-center justify-center gap-xs rounded-lg border border-border bg-surface p-xs shadow-pop"
    >
      <Button
        variant="outline"
        size="sm"
        class="cursor-grab active:cursor-grabbing"
        draggable="true"
        :data-testid="`dashboard-drag-section-${rowId}`"
        @dragstart="emit('move-start', $event)"
        @dragend="emit('move-end')"
      >
        <Icon name="grip-vertical" :size="13" />
        {{ t('dashboards.grid.move') }}
      </Button>
      <Button
        variant="outline"
        size="sm"
        data-testid="dashboard-duplicate-section"
        @click="emit('duplicate')"
      >
        <Icon name="copy" :size="13" />
        {{ t('dashboards.grid.duplicate') }}
      </Button>
      <Button
        variant="outline"
        size="sm"
        data-testid="dashboard-remove-block"
        @click="emit('remove')"
      >
        <Icon name="trash" :size="13" />
        {{ t('dashboards.grid.delete') }}
      </Button>
      <Button
        v-if="editable"
        variant="primary"
        size="sm"
        :data-testid="empty ? 'dashboard-add-widget' : 'dashboard-edit-widget'"
        @click="emit('edit')"
      >
        <Icon :name="empty ? 'plus' : 'pencil'" :size="13" />
        {{ empty ? t('dashboards.grid.addBlock') : t('dashboards.grid.editBlock') }}
      </Button>
    </div>
  </div>
</template>
